import { ReactComponent as Logo } from 'assets/images/logo.svg';
import { classnames, getInitials, isOwner } from 'helpers/utils';
import { VideoDisabledIcon } from 'assets/icons';
import { SensorSelectionContext } from 'context';
import { useContext } from 'react';
import styles from './no-cam-enabled.module.scss';

type NoCamEnabledProps = {
  user: UserType;
  vault: VaultType;
  myEmptyView: boolean;
  small?: boolean;
  className?: string;
  otherUserName?: string;
  otherUserIsGuest: boolean;
};

const NoCamEnabled: React.FC<NoCamEnabledProps> = ({
  user,
  vault,
  myEmptyView,
  small = false,
  className = '',
  otherUserName,
  otherUserIsGuest,
}) => {
  const { sensorSelection } = useContext(SensorSelectionContext);
  const cameraDeviceAvailable = myEmptyView
    ? !sensorSelection.camera.error
    : true;

  const showDefaultPicture = () => (
    <div className={styles.defaultpicture}>
      <Logo className={styles.logo} />
    </div>
  );

  const showGuestContentDisable = () => (
    <div className={classnames('text__heading1__gray500', styles.guest)}>
      {vault.coverUrl ? (
        <img src={vault.coverUrl} className={styles.picture} alt="logo" />
      ) : (
        showDefaultPicture()
      )}
    </div>
  );

  const showMyEmptyAvatar = () =>
    user.id ? (
      <div className={styles.initialsText}>{getInitials(user.name)}</div>
    ) : (
      showGuestContentDisable()
    );

  const showOtherUserEmptyAvatar = () =>
    otherUserName && !otherUserIsGuest ? (
      <div className={styles.initialsText}>{getInitials(otherUserName)}</div>
    ) : (
      showGuestContentDisable()
    );

  const showMyEmptyText = () => (
    <div className={classnames(styles.infoText)}>
      {isOwner(user, vault) && !cameraDeviceAvailable
        ? 'No camera device detected'
        : 'Your camera is off'}
    </div>
  );

  const showNoCameraIcon = () => (
    <div className={styles.noCamCircle}>
      <VideoDisabledIcon className={styles.videoEnabled} />
    </div>
  );

  const showCameraOffText = (name: string) => {
    const lastLetter = name.slice(-1);
    const properName = lastLetter === 's' ? `${name}’` : `${name}’s`;

    return `${properName} camera is off`;
  };

  const showOtherUserEmptyText = () => (
    <div className={styles.infoText}>
      {showCameraOffText(otherUserName ?? '')}
    </div>
  );

  return (
    <div className={classnames(className, styles.noCamEnabled)}>
      <div className={classnames(styles.avatar, { [styles.smallView]: small })}>
        {myEmptyView ? showMyEmptyAvatar() : showOtherUserEmptyAvatar()}
        {showNoCameraIcon()}
      </div>
      <div className={styles.text}>
        {myEmptyView ? showMyEmptyText() : showOtherUserEmptyText()}
      </div>
    </div>
  );
};

export { NoCamEnabled };
